
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../../../store/store';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  Divider,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Save, Edit, Upload, PlayArrow, Schedule, Person } from '@mui/icons-material';
import { attendanceApi } from '../../../api/attendanceApi';
import { shiftApi } from '../../../api/shiftApi';
import { leaveApi } from '../../../api/leaveApi';
import { LeaveEntitlementDto } from '../../../types';
import { toast } from 'react-toastify';

// ========== Rules Tab ==========
const RulesTab: React.FC = () => {
  const [ruleId, setRuleId] = useState<string | undefined>();
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [shifts, setShifts] = useState<Array<{ id: string; shiftName: string; isActive?: boolean }>>([]);
  const [minWorkingHours, setMinWorkingHours] = useState('8');
  const [lateArrivalGrace, setLateArrivalGrace] = useState('15');
  const [lateArrivalPenalty, setLateArrivalPenalty] = useState('half-day');
  const [lateMarkDeductionEnabled, setLateMarkDeductionEnabled] = useState(false);
  const [lateMarksThresholdCount, setLateMarksThresholdCount] = useState('3');
  const [lateMarkDeductionType, setLateMarkDeductionType] = useState('half-day');
  const [earlyCheckoutGrace, setEarlyCheckoutGrace] = useState('10');
  const [earlyCheckoutPenalty, setEarlyCheckoutPenalty] = useState('half-day');
  const [overtimeEnabled, setOvertimeEnabled] = useState(true);
  const [minimumOvertimeMinutes, setMinimumOvertimeMinutes] = useState('30');
  const [maxOvertimeHours, setMaxOvertimeHours] = useState('4');
  const [regularizationRequestLimit, setRegularizationRequestLimit] = useState('0');
  const [regularizationLimitPeriod, setRegularizationLimitPeriod] = useState('0');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      let data = await attendanceApi.getAttendanceRules(selectedShiftId || undefined);
      if (selectedShiftId && !data?.id) {
        const globalRules = await attendanceApi.getAttendanceRules();
        data = { ...globalRules, id: undefined, shiftId: selectedShiftId };
      }

      if (data) {
        setRuleId(data.id);
        setMinWorkingHours(String(data.minWorkingHours ?? '8'));
        setLateArrivalGrace(String(data.lateArrivalGraceMinutes ?? '15'));
        setLateArrivalPenalty(data.lateArrivalPenalty ?? 'half-day');
        setLateMarkDeductionEnabled(data.lateMarkDeductionEnabled ?? false);
        setLateMarksThresholdCount(String(data.lateMarksThresholdCount ?? '3'));
        setLateMarkDeductionType(data.lateMarkDeductionType ?? 'half-day');
        setEarlyCheckoutGrace(String(data.earlyCheckoutGraceMinutes ?? '10'));
        setEarlyCheckoutPenalty(data.earlyCheckoutPenalty ?? 'half-day');
        setOvertimeEnabled(data.overtimeEnabled ?? true);
        setMinimumOvertimeMinutes(String(data.minimumOvertimeMinutes ?? '30'));
        setMaxOvertimeHours(String(data.maxOvertimeHours ?? '4'));
        setRegularizationRequestLimit(String(data.regularizationRequestLimit ?? '0'));
        setRegularizationLimitPeriod(String(data.regularizationLimitPeriod ?? '0'));
      }
    } catch {
      setRuleId(undefined);
      setMinWorkingHours('8');
      setLateArrivalGrace('15');
      setLateArrivalPenalty('half-day');
      setLateMarkDeductionEnabled(false);
      setLateMarksThresholdCount('3');
      setLateMarkDeductionType('half-day');
      setEarlyCheckoutGrace('10');
      setEarlyCheckoutPenalty('half-day');
      setOvertimeEnabled(true);
      setMinimumOvertimeMinutes('30');
      setMaxOvertimeHours('4');
      setRegularizationRequestLimit('0');
      setRegularizationLimitPeriod('0');
      // Use defaults if not configured yet
    } finally {
      setLoading(false);
    }
  }, [selectedShiftId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  useEffect(() => {
    let active = true;

    shiftApi
      .getShifts()
      .then((items) => {
        if (active) {
          setShifts((items || []).filter((shift: any) => shift.isActive !== false));
        }
      })
      .catch(() => {
        if (active) {
          setShifts([]);
          toast.error('Failed to load shifts');
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await attendanceApi.saveAttendanceRules({
        id: ruleId,
        shiftId: selectedShiftId || undefined,
        minWorkingHours: Number(minWorkingHours),
        lateArrivalGraceMinutes: Number(lateArrivalGrace),
        lateArrivalPenalty,
        lateMarkDeductionEnabled,
        lateMarksThresholdCount: Math.max(0, Number(lateMarksThresholdCount) || 0),
        lateMarkDeductionType,
        earlyCheckoutGraceMinutes: Number(earlyCheckoutGrace),
        earlyCheckoutPenalty,
        overtimeEnabled,
        minimumOvertimeMinutes: Number(minimumOvertimeMinutes),
        maxOvertimeHours: Number(maxOvertimeHours),
        regularizationRequestLimit: Math.max(0, Number(regularizationRequestLimit) || 0),
        regularizationLimitPeriod: Number(regularizationLimitPeriod),
      });
      toast.success('Rules saved successfully!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Attendance Rules
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure minimum working hours, late arrival policies, and early checkout rules.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Shift"
              select
              fullWidth
              size="small"
              value={selectedShiftId}
              onChange={(e) => setSelectedShiftId(e.target.value)}
              helperText="Select a shift to configure shift-wise attendance rules"
            >
              <MenuItem value="">All Shifts</MenuItem>
              {shifts.map((shift) => (
                <MenuItem key={shift.id} value={shift.id}>
                  {shift.shiftName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Working Hours */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Working Hours
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
           <TextField
              label="Total Working Hours"
              type="number"
              fullWidth
              size="small"
              value={minWorkingHours}
              onChange={(e) => setMinWorkingHours(e.target.value)}
              inputProps={{ min: 0, max: 24 }}
              helperText="Hours required for full day attendance"
            />
          </Grid>

          {/* Late Arrival Policy */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Late Arrival Policy
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Grace Period (minutes)"
              type="number"
              fullWidth
              size="small"
              value={lateArrivalGrace}
              onChange={(e) => setLateArrivalGrace(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Penalty Type"
              select
              fullWidth
              size="small"
              value={lateArrivalPenalty}
              onChange={(e) => setLateArrivalPenalty(e.target.value)}
            >
              <MenuItem value="none">No Penalty</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="half-day">Half Day Leave Deduction</MenuItem>
              <MenuItem value="full-day">Full Day Leave Deduction</MenuItem>
            </TextField>
          </Grid>

          {/* Monthly Late Mark Deduction */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Monthly Late Mark Salary Deduction
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={lateMarkDeductionEnabled}
                  onChange={(e) => setLateMarkDeductionEnabled(e.target.checked)}
                />
              }
              label="Enable Late Mark Deduction"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Off by default — no impact on payroll unless enabled.
            </Typography>
          </Grid>
          {lateMarkDeductionEnabled && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Late Marks Allowed per Month"
                  type="number"
                  fullWidth
                  size="small"
                  value={lateMarksThresholdCount}
                  onChange={(e) => setLateMarksThresholdCount(e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText="Deduction repeats for every this many late marks in a month (e.g. 15 late marks at 3 = 5x deduction)"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Deduction Applied"
                  select
                  fullWidth
                  size="small"
                  value={lateMarkDeductionType}
                  onChange={(e) => setLateMarkDeductionType(e.target.value)}
                >
                  <MenuItem value="half-day">Half Day Salary Deduction</MenuItem>
                  <MenuItem value="full-day">Full Day Salary Deduction</MenuItem>
                </TextField>
              </Grid>
            </>
          )}

          {/* Early Checkout Policy */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Early Checkout Rules
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Grace Period (minutes)"
              type="number"
              fullWidth
              size="small"
              value={earlyCheckoutGrace}
              onChange={(e) => setEarlyCheckoutGrace(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Penalty Type"
              select
              fullWidth
              size="small"
              value={earlyCheckoutPenalty}
              onChange={(e) => setEarlyCheckoutPenalty(e.target.value)}
            >
              <MenuItem value="none">No Penalty</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="half-day">Half Day Leave Deduction</MenuItem>
              <MenuItem value="full-day">Full Day Leave Deduction</MenuItem>
            </TextField>
          </Grid>

          {/* Overtime Settings */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Overtime Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControlLabel
              control={<Switch checked={overtimeEnabled} onChange={(e) => setOvertimeEnabled(e.target.checked)} />}
              label="Enable Overtime"
            />
          </Grid>
          {overtimeEnabled && (
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Minimum OT Minutes"
                type="number"
                fullWidth
                size="small"
                value={minimumOvertimeMinutes}
                onChange={(e) => setMinimumOvertimeMinutes(e.target.value)}
                inputProps={{ min: 0 }}
                helperText="Minimum extra time required before OT is eligible"
              />
            </Grid>
          )}
          {overtimeEnabled && (
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Max Overtime Hours (per day)"
                type="number"
                fullWidth
                size="small"
                value={maxOvertimeHours}
                onChange={(e) => setMaxOvertimeHours(e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
          )}

          {/* Regularization Settings */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Regularization Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Regularization Permission Limit"
              type="number"
              fullWidth
              size="small"
              value={regularizationRequestLimit}
              onChange={(e) => setRegularizationRequestLimit(e.target.value)}
              inputProps={{ min: 0 }}
              helperText="Allowed requests per employee; 0 means unlimited"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Limit Period"
              select
              fullWidth
              size="small"
              value={regularizationLimitPeriod}
              onChange={(e) => setRegularizationLimitPeriod(e.target.value)}
              helperText="Window used to count employee requests"
            >
              <MenuItem value="0">Monthly</MenuItem>
              <MenuItem value="1">Weekly</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          sx={{ mt: 3 , textTransform: 'none' }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Rules'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ========== Entitled Setting Tab ==========
const EntitledSettingTab: React.FC = () => {
  const [entitlements, setEntitlements] = useState<LeaveEntitlementDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editItem, setEditItem] = useState<LeaveEntitlementDto | null>(null);
  const [editTotalDays, setEditTotalDays] = useState('');
  const [editCarryForward, setEditCarryForward] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const fetchEntitlements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leaveApi.getLeaveEntitlements({ searchTerm: searchTerm || undefined });
      setEntitlements(data.items);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch entitlements');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  const handleEditOpen = (ent: LeaveEntitlementDto) => {
    setEditItem(ent);
    setEditTotalDays(String(ent.totalDays));
    setEditCarryForward(String(ent.carryForwardDays));
  };

  const handleEditSave = async () => {
    if (!editItem) return;
    setEditSaving(true);
    try {
      await leaveApi.updateLeaveEntitlement(editItem.id, {
        id: editItem.id,
        totalDays: Number(editTotalDays),
        carryForwardDays: Number(editCarryForward),
        isActive: editItem.isActive,
      });
      toast.success('Entitlement updated');
      setEditItem(null);
      fetchEntitlements();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Person color="primary" />
        <Typography variant="h6" fontWeight="bold">
          Employee Leave Entitlements
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure leave entitlements for each employee based on leave types.
      </Typography>

      <TextField
        label="Search Employee"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, width: 300 }}
        placeholder="Search by name or ID..."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Employee Code</strong></TableCell>
                <TableCell><strong>Employee Name</strong></TableCell>
                <TableCell><strong>Leave Type</strong></TableCell>
                <TableCell align="center"><strong>Total</strong></TableCell>
                <TableCell align="center"><strong>Used</strong></TableCell>
                <TableCell align="center"><strong>Remaining</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entitlements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No entitlements found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entitlements.map((ent) => (
                  <TableRow key={ent.id} hover>
                    <TableCell>{ent.employeeCode}</TableCell>
                    <TableCell>{ent.employeeName}</TableCell>
                    <TableCell>{ent.leaveTypeName}</TableCell>
                    <TableCell align="center">{ent.totalDays}</TableCell>
                    <TableCell align="center">{ent.usedDays}</TableCell>
                    <TableCell align="center">
                      <Chip label={ent.remainingDays} color={ent.remainingDays > 0 ? 'success' : 'error'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Entitlement">
                        <IconButton size="small" color="primary" onClick={() => handleEditOpen(ent)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Entitlement</DialogTitle>
        <DialogContent>
          {editItem && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {editItem.employeeName} — {editItem.leaveTypeName}
              </Typography>
              <TextField
                label="Total Days"
                type="number"
                fullWidth
                size="small"
                value={editTotalDays}
                onChange={(e) => setEditTotalDays(e.target.value)}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Carry Forward Days"
                type="number"
                fullWidth
                size="small"
                value={editCarryForward}
                onChange={(e) => setEditCarryForward(e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={16} /> : undefined}
          >
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ========== Advance Setting Tab ==========
const AdvanceSettingTab: React.FC = () => {
  const [halfDayEnabled, setHalfDayEnabled] = useState(true);
  //const [halfDayHours, setHalfDayHours] = useState('4');
  const [halfDayHours, setHalfDayHours] = useState<number | "">("");
  const [accrualEnabled, setAccrualEnabled] = useState(true);
  const [accrualFrequency, setAccrualFrequency] = useState('monthly');
  //const [accrualRate, setAccrualRate] = useState('1');
  const [carryForwardEnabled, setCarryForwardEnabled] = useState(true);
  // const [maxCarryForward, setMaxCarryForward] = useState('5');
  // const [carryForwardExpiry, setCarryForwardExpiry] = useState('90');
  const [negativeLeavesEnabled, setNegativeLeavesEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accrualRate, setAccrualRate] = useState<number | "">("");
  const [maxCarryForward, setMaxCarryForward] = useState<number | "">("");
  const [carryForwardExpiry, setCarryForwardExpiry] = useState<number | "">("");

  // Holiday / Week-Off Work Compensation (Comp-Off)
  const [holidayWorkCompensation, setHolidayWorkCompensation] = useState<'CompOff' | 'Overtime' | 'EmployeeChoice' | 'None'>('CompOff');
  const [compOffHalfHours, setCompOffHalfHours] = useState<number | "">("");
  const [compOffFullHours, setCompOffFullHours] = useState<number | "">("");
  const [compOffRejectBelowMin, setCompOffRejectBelowMin] = useState(false);
  const [compOffExpiryDays, setCompOffExpiryDays] = useState<number | "">("");
  const [compOffAdminAutoApprove, setCompOffAdminAutoApprove] = useState(true);

  const [fieldErrors, setFieldErrors] = useState<{
    halfDayHours?: string;
    accrualRate?: string;
    maxCarryForward?: string;
    carryForwardExpiry?: string;
    compOffFullHours?: string;
  }>({});
  const compOffActive = holidayWorkCompensation === 'CompOff' || holidayWorkCompensation === 'EmployeeChoice';
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await attendanceApi.getAdvanceSettings();
      if (data) {
        setHalfDayEnabled(data.halfDayEnabled ?? true);
        setHalfDayHours(data.halfDayHours ?? 4);
        setAccrualEnabled(data.accrualEnabled ?? true);
        setAccrualFrequency(data.accrualFrequency ?? 'monthly');
        setAccrualRate(data.accrualRate ?? 1);
        setCarryForwardEnabled(data.carryForwardEnabled ?? true);
        setMaxCarryForward(data.maxCarryForwardDays ?? 5);
        setCarryForwardExpiry(data.carryForwardExpiryDays ?? 90);
        setNegativeLeavesEnabled(data.negativeLeavesEnabled ?? false);
        setHolidayWorkCompensation(data.holidayWorkCompensation ?? 'CompOff');
        setCompOffHalfHours(data.compOffMinHoursForHalfCredit ?? 4);
        setCompOffFullHours(data.compOffMinHoursForFullCredit ?? 8);
        setCompOffRejectBelowMin(data.compOffRejectBelowMinHours ?? false);
        setCompOffExpiryDays(data.compOffExpiryDays ?? 90);
        setCompOffAdminAutoApprove(data.compOffAdminAutoApprove ?? true);
      }
    } catch {
      // Use defaults if not configured yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    const nextErrors: {
      halfDayHours?: string;
      accrualRate?: string;
      maxCarryForward?: string;
      carryForwardExpiry?: string;
      compOffFullHours?: string;
    } = {};

    // if (halfDayEnabled && (halfDayHours === '' || Number(halfDayHours) < 4 || Number(halfDayHours) > 24)) {
    //   nextErrors.halfDayHours = 'Half Day Hours is required (4 to 24).';
    // }

    if (accrualEnabled && (accrualRate === '' || Number(accrualRate) < 1)) {
      nextErrors.accrualRate = 'Days per Accrual is required (minimum 1).';
    }

    if (carryForwardEnabled && (maxCarryForward === '' || Number(maxCarryForward) < 1)) {
      nextErrors.maxCarryForward = 'Max Carry Forward Days is required (minimum 1).';
    }

    if (carryForwardEnabled && (carryForwardExpiry === '' || Number(carryForwardExpiry) < 0)) {
      nextErrors.carryForwardExpiry = 'Carry Forward Expiry is required.';
    }

    if (compOffActive && compOffFullHours !== '' && compOffHalfHours !== ''
        && Number(compOffFullHours) < Number(compOffHalfHours)) {
      nextErrors.compOffFullHours = 'Full-credit hours must be >= half-credit hours.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      toast.error('Please fill required fields for enabled settings.');
      return;
    }

    setFieldErrors({});
    setSaving(true);
    try {
      await attendanceApi.saveAdvanceSettings({
        halfDayEnabled,
        halfDayHours: Number(halfDayHours),
        accrualEnabled,
        accrualFrequency,
        accrualRate: Number(accrualRate),
        carryForwardEnabled,
        maxCarryForwardDays: carryForwardEnabled ? Number(maxCarryForward) : 0,
        carryForwardExpiryDays: carryForwardEnabled ? Number(carryForwardExpiry) : 0,
        negativeLeavesEnabled,
        holidayWorkCompensation,
        compOffMinHoursForHalfCredit: compOffHalfHours === '' ? 4 : Number(compOffHalfHours),
        compOffMinHoursForFullCredit: compOffFullHours === '' ? 8 : Number(compOffFullHours),
        compOffRejectBelowMinHours: compOffRejectBelowMin,
        compOffExpiryDays: compOffExpiryDays === '' ? 90 : Number(compOffExpiryDays),
        compOffAdminAutoApprove,
      });
      toast.success('Advance settings saved successfully!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Advance Leave Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure half-day leave, leave accrual, and carry-forward policies.
        </Typography>

        <Grid container spacing={3}>
          {/* Half Day Leave */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Half Day Leave
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControlLabel
              control={<Switch checked={halfDayEnabled} onChange={(e) => setHalfDayEnabled(e.target.checked)} />}
              label="Enable Half Day Leave"
            />
          </Grid>
          {halfDayEnabled && (
            <Grid size={{ xs: 12, md: 4 }}>
             <TextField
              label="Half Day Hours"
              type="number"
              fullWidth
              size="small"
              value={halfDayHours}
              onChange={(e) => {
                const value = e.target.value;
                setFieldErrors((prev) => ({ ...prev, halfDayHours: undefined }));

                // Allow clearing the input
                if (value === "") {
                  setHalfDayHours("");
                  return;
                }

                const num = Number(value);

                // Block negative numbers
                if (num < 0) return;

                // Allow typing any number, max validation will be done onBlur
                setHalfDayHours(num);
              }}
              // onBlur={() => {
              //   if (halfDayHours === "") return;

              //   let num = halfDayHours;

              //   // enforce minimum 4
              //   if (num < 4) num = 4;

              //   // enforce maximum 24
              //   if (num > 6) num = 6;

              //   setHalfDayHours(num);
              // }}
              //inputProps={{ min: 4, max: 6, step: 1 }}
              error={Boolean(fieldErrors.halfDayHours)}
              helperText={fieldErrors.halfDayHours || ''}
            />
            </Grid>
          )}
          {/* Leave Accrual */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Leave Accrual
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControlLabel
              control={<Switch checked={accrualEnabled} onChange={(e) => setAccrualEnabled(e.target.checked)} />}
              label="Enable Leave Accrual"
            />
          </Grid>
          {accrualEnabled && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Accrual Frequency"
                  select
                  fullWidth
                  size="small"
                  value={accrualFrequency}
                  onChange={(e) => setAccrualFrequency(e.target.value)}
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Days per Accrual"
                    type="number"
                    fullWidth
                    size="small"
                    value={accrualRate}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldErrors((prev) => ({ ...prev, accrualRate: undefined }));

                      // Allow empty
                      if (value === "") {
                        setAccrualRate("");
                        return;
                      }

                      let num = Number(value);

                      // ❌ block less than 1
                      if (num < 1) return;

                      setAccrualRate(num);
                    }}
                    onKeyDown={(e) => {
                      // ❌ prevent typing negative sign
                      if (e.key === '-' || e.key === 'Minus') {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{ min: 1, step: 1 }}
                    error={Boolean(fieldErrors.accrualRate)}
                    helperText={fieldErrors.accrualRate || 'Minimum value is 1'}
                  />
                </Grid>
            </>
          )}

          {/* Carry Forward */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Carry Forward Policy
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControlLabel
              control={<Switch checked={carryForwardEnabled} onChange={(e) => {
                const enabled = e.target.checked;
                setCarryForwardEnabled(enabled);

                if (!enabled) {
                  setMaxCarryForward(0);
                  setCarryForwardExpiry(0);
                  setFieldErrors((prev) => ({
                    ...prev,
                    maxCarryForward: undefined,
                    carryForwardExpiry: undefined,
                  }));
                } else {
                  setFieldErrors((prev) => ({
                    ...prev,
                    maxCarryForward:
                      maxCarryForward === '' || Number(maxCarryForward) < 1
                        ? 'Max Carry Forward Days is required (minimum 1).'
                        : undefined,
                  }));
                }
              }} />}
              label="Enable Carry Forward"
            />
          </Grid>
            {carryForwardEnabled && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Max Carry Forward Days"
                    type="number"
                    fullWidth
                    size="small"
                    value={maxCarryForward}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "") {
                        setMaxCarryForward("");
                        setFieldErrors((prev) => ({
                          ...prev,
                          maxCarryForward: carryForwardEnabled ? 'Max Carry Forward Days is required (minimum 1).' : undefined,
                        }));
                        return;
                      }

                      const num = Number(value);

                      // ❌ block negative values
                      if (num < 0) return;

                      setMaxCarryForward(num);
                      setFieldErrors((prev) => ({
                        ...prev,
                        maxCarryForward:
                          carryForwardEnabled && num < 1
                            ? 'Max Carry Forward Days is required (minimum 1).'
                            : undefined,
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'Minus') {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{ min: 1 }}
                    error={Boolean(fieldErrors.maxCarryForward)}
                    helperText={fieldErrors.maxCarryForward || 'Minimum value is 1'}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Expiry (days from new year)"
                    type="number"
                    fullWidth
                    size="small"
                    value={carryForwardExpiry}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldErrors((prev) => ({ ...prev, carryForwardExpiry: undefined }));

                      if (value === "") {
                        setCarryForwardExpiry("");
                        return;
                      }

                      const num = Number(value);

                      // ❌ block negative
                      if (num < 0) return;

                      setCarryForwardExpiry(num);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'Minus') {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{ min: 0 }}
                    error={Boolean(fieldErrors.carryForwardExpiry)}
                    helperText={fieldErrors.carryForwardExpiry || '0 = no expiry'}
                  />
                </Grid>
              </>
            )}
          {/* Holiday / Week-Off Work */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Holiday / Week-Off Work
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              How an employee is compensated for working on a holiday or week-off.
              Applies to every employee on every shift.
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Compensation Mode"
              select
              fullWidth
              size="small"
              value={holidayWorkCompensation}
              onChange={(e) => setHolidayWorkCompensation(e.target.value as typeof holidayWorkCompensation)}
            >
              <MenuItem value="CompOff">Comp-Off (earn time off)</MenuItem>
              <MenuItem value="Overtime">Overtime (pay the hours)</MenuItem>
              <MenuItem value="EmployeeChoice">Employee Choice (one or the other)</MenuItem>
              <MenuItem value="None">None</MenuItem>
            </TextField>
          </Grid>
          {compOffActive && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Hours worked for half-day credit"
                  type="number"
                  fullWidth
                  size="small"
                  value={compOffHalfHours}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") { setCompOffHalfHours(""); return; }
                    const num = Number(value);
                    if (num < 0) return;
                    setCompOffHalfHours(num);
                  }}
                  onKeyDown={(e) => { if (e.key === '-' || e.key === 'Minus') e.preventDefault(); }}
                  inputProps={{ min: 0, step: 0.5 }}
                  helperText="Work >= this -> 0.5 day"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Hours worked for full-day credit"
                  type="number"
                  fullWidth
                  size="small"
                  value={compOffFullHours}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFieldErrors((prev) => ({ ...prev, compOffFullHours: undefined }));
                    if (value === "") { setCompOffFullHours(""); return; }
                    const num = Number(value);
                    if (num < 0) return;
                    setCompOffFullHours(num);
                  }}
                  onKeyDown={(e) => { if (e.key === '-' || e.key === 'Minus') e.preventDefault(); }}
                  inputProps={{ min: 0, step: 0.5 }}
                  error={Boolean(fieldErrors.compOffFullHours)}
                  helperText={fieldErrors.compOffFullHours || 'Work >= this -> 1 day'}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Comp-Off expiry (days from earned)"
                  type="number"
                  fullWidth
                  size="small"
                  value={compOffExpiryDays}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") { setCompOffExpiryDays(""); return; }
                    const num = Number(value);
                    if (num < 0) return;
                    setCompOffExpiryDays(num);
                  }}
                  onKeyDown={(e) => { if (e.key === '-' || e.key === 'Minus') e.preventDefault(); }}
                  inputProps={{ min: 0, step: 1 }}
                  helperText="0 = never expires"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={<Switch checked={compOffRejectBelowMin} onChange={(e) => setCompOffRejectBelowMin(e.target.checked)} />}
                  label="Reject request if hours below half-day threshold"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={<Switch checked={compOffAdminAutoApprove} onChange={(e) => setCompOffAdminAutoApprove(e.target.checked)} />}
                  label="Auto-approve when raised by Admin/HR"
                />
              </Grid>
            </>
          )}

          {/* Negative Leaves */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Other Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControlLabel
              control={<Switch checked={negativeLeavesEnabled} onChange={(e) => setNegativeLeavesEnabled(e.target.checked)} />}
              label="Allow Negative Leave Balance"
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          sx={{ mt: 3 ,textTransform: 'none' }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Advance Settings'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ========== Auto Attendance Process Tab ==========
const AutoAttendanceProcessTab: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processDate, setProcessDate] = useState(new Date().toISOString().split('T')[0]);
  const [scheduleTime, setScheduleTime] = useState('23:00');
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        const data = await attendanceApi.getAutoProcessSchedule();
        if (data) {
          setScheduleTime(data.runTime || '23:00');
          setScheduleEnabled(data.enabled ?? true);
        }
      } catch {
        // Use defaults
      }
    };
    loadSchedule();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }
    setImporting(true);
    try {
      const result = await attendanceApi.importBiometricLogs(selectedFile);
      toast.success(`File "${selectedFile.name}" imported — ${result.imported} records processed`);
      setSelectedFile(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  const handleProcessAttendance = async () => {
    setProcessing(true);
    try {
      const result = await attendanceApi.runAutoProcess(processDate);
      toast.success(`Attendance processed for ${processDate} — ${result.processed} records`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to process attendance');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveSchedule = async () => {
    setScheduleSaving(true);
    try {
      await attendanceApi.saveAutoProcessSchedule({ runTime: scheduleTime, enabled: scheduleEnabled });
      toast.success('Schedule saved');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Auto Attendance Process
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Import biometric/punch logs, process attendance data, and calculate working hours.
      </Typography>

      <Grid container spacing={3}>
        {/* Import Biometric Logs */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Upload color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Import Biometric Logs
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload CSV/Excel file containing employee punch data.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Button variant="outlined" component="label" disabled={importing}>
                  Select File
                  <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFileSelect} />
                </Button>
                {selectedFile && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedFile.name}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                color="primary"
                startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <Upload />}
                onClick={handleImport}
                disabled={!selectedFile || importing}
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Process Attendance */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PlayArrow color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Process Attendance
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Process punch data and calculate working hours for a specific date.
              </Typography>

              <TextField
                label="Process Date"
                type="date"
                size="small"
                value={processDate}
                onChange={(e) => setProcessDate(e.target.value)}
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />

              {processing && <LinearProgress sx={{ mb: 2 }} />}

              <Button
                variant="contained"
                color="success"
                startIcon={<PlayArrow />}
                onClick={handleProcessAttendance}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Process Attendance'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Schedule Auto Process */}
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Schedule color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Scheduled Auto Process
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure automatic attendance processing schedule.
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Run Time"
                    type="time"
                    fullWidth
                    size="small"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControlLabel
                    control={<Switch checked={scheduleEnabled} onChange={(e) => setScheduleEnabled(e.target.checked)} />}
                    label="Enable Daily Auto Process"
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="primary"
                startIcon={scheduleSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                sx={{ mt: 2 , textTransform: 'none' }}
                onClick={handleSaveSchedule}
                disabled={scheduleSaving}
              >
                {scheduleSaving ? 'Saving...' : 'Save Schedule'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const AttendanceRulesPage: React.FC = () => {
  useSelector((state: RootState) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromPath = (path: string): number => {
    if (path.includes('/attendance/auto-process')) return 2;
    if (path.includes('/attendance/advance-setting')) return 1;
    return 0;
  };

  const [tab, setTab] = useState(() => getTabFromPath(location.pathname));

  useEffect(() => {
    setTab(getTabFromPath(location.pathname));
  }, [location.pathname]);
  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Attendance Rules & Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure attendance rules and automated processing.        
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          if (v === 0) navigate('/attendance/rules');
          if (v === 1) navigate('/attendance/advance-setting');
          if (v === 2) navigate('/attendance/auto-process');
        }}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none', // ✅ this fixes it
            fontWeight: 700,
          },
        }}
      >
      <Tab label="Rules" />
      <Tab label="Advanced Settings" />
      {/* <Tab label="Auto Attendance Process" /> */}
    </Tabs>
      <Box>
        {tab === 0 && <RulesTab />}
        {/* {tab === 1 && <EntitledSettingTab />} */}
        {tab === 1 && <AdvanceSettingTab />}
        {/* {tab === 2 && <AutoAttendanceProcessTab />} */}
      </Box>
    </Box>
  );
};

export default AttendanceRulesPage;
